import React, { useState, useEffect } from "react";
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Paper,
  CircularProgress, Chip, IconButton, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Tooltip, Alert, Snackbar,
} from "@mui/material";
import { Check, Close, Delete, Edit, Visibility, Refresh, Search, Phone, Email, CheckCircle } from "@mui/icons-material";
import axios from "axios";
import { api_urls } from "../../../utils/api-urls";

const API = api_urls + "api/sanatani-hotel";

const SSPartners = () => {
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [editDialog, setEditDialog] = useState(false);
  const [editPartner, setEditPartner] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [detailPartner, setDetailPartner] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const [search, setSearch] = useState("");

  useEffect(() => { fetchPartners(); }, []);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API + "/admin/partners");
      if (res.data?.success) setPartners(res.data.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleAction = async (id, action) => {
    try {
      await axios.put(API + "/admin/partner/" + id + "/" + action);
      setSnack({ open: true, msg: "Partner " + action + " successfully!", severity: "success" });
      fetchPartners();
    } catch (e) { setSnack({ open: true, msg: "Error: " + (e.response?.data?.message || e.message), severity: "error" }); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(API + "/admin/partner/" + deleteId);
      setSnack({ open: true, msg: "Partner deleted successfully!", severity: "success" });
      setDeleteDialog(false);
      setDeleteId(null);
      fetchPartners();
    } catch (e) { setSnack({ open: true, msg: "Error deleting partner", severity: "error" }); }
  };

  const handleEditSave = async () => {
    try {
      await axios.put(API + "/admin/partner/" + editPartner._id + "/edit", {
        businessName: editPartner.businessName,
        ownerName: editPartner.ownerName,
        mobile: editPartner.mobile,
        email: editPartner.email,
        businessType: editPartner.businessType,
        address: editPartner.address,
        commissionRate: editPartner.commissionRate !== undefined ? Number(editPartner.commissionRate) : 15,
      });
      setSnack({ open: true, msg: "Partner updated!", severity: "success" });
      setEditDialog(false);
      fetchPartners();
    } catch (e) { setSnack({ open: true, msg: "Error updating partner", severity: "error" }); }
  };

  const handleViewDetail = async (id) => {
    try {
      const res = await axios.get(API + "/admin/partner/" + id);
      if (res.data?.success) { setDetailPartner(res.data.data.partner || res.data.data); setDetailDialog(true); }
    } catch (e) { console.error(e); }
  };

  const filtered = partners.filter(p => 
    !search || p.businessName?.toLowerCase().includes(search.toLowerCase()) || 
    p.ownerName?.toLowerCase().includes(search.toLowerCase()) || 
    p.mobile?.includes(search)
  );

  if (loading) return <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>🤝 Sanatani Sadan - Partners ({partners.length})</Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchPartners}>Refresh</Button>
      </Box>

      <Box display="flex" gap={2} mb={2}>
        <TextField size="small" placeholder="Search by name, mobile..." value={search} onChange={e => setSearch(e.target.value)} 
          InputProps={{ startAdornment: <Search sx={{ color: "#999", mr: 1 }} /> }} sx={{ minWidth: 300 }} />
      </Box>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell><b>#</b></TableCell>
              <TableCell><b>Business Name</b></TableCell>
              <TableCell><b>Owner</b></TableCell>
              <TableCell><b>Mobile</b></TableCell>
              <TableCell><b>Type</b></TableCell>
              <TableCell><b>City</b></TableCell>
              <TableCell><b>Commission %</b></TableCell><TableCell><b>Status</b></TableCell>
              <TableCell><b>Verified</b></TableCell>
              <TableCell><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((p, i) => (
              <TableRow key={p._id} hover>
                <TableCell>{i + 1}</TableCell>
                <TableCell><Typography fontWeight={600} fontSize={13}>{p.businessName}</Typography></TableCell>
                <TableCell>{p.ownerName}</TableCell>
                <TableCell>{p.mobile}</TableCell>
                <TableCell><Chip label={p.businessType} size="small" variant="outlined" /></TableCell>
                <TableCell>{p.address?.city || "-"}</TableCell>
                <TableCell>{p.commissionRate !== undefined ? p.commissionRate + "%" : "15%"}</TableCell>
                <TableCell>
                  <Chip label={p.isApproved ? "Approved" : "Pending"} size="small" 
                    color={p.isApproved ? "success" : "warning"} />
                </TableCell>
                <TableCell>
                  <Chip label={p.isVerified ? "Yes" : "No"} size="small" 
                    color={p.isVerified ? "success" : "default"} />
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    <Tooltip title="View Details"><IconButton size="small" onClick={() => handleViewDetail(p._id)}><Visibility fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => { setEditPartner({...p}); setEditDialog(true); }}><Edit fontSize="small" /></IconButton></Tooltip>
                    {!p.isApproved && <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => handleAction(p._id, "approve")}><Check fontSize="small" /></IconButton></Tooltip>}
                    {p.isApproved && <Tooltip title="Reject"><IconButton size="small" color="warning" onClick={() => handleAction(p._id, "reject")}><Close fontSize="small" /></IconButton></Tooltip>}
                    {!p.isVerified && <Tooltip title="Verify"><IconButton size="small" sx={{color: '#1565C0'}} onClick={() => handleAction(p._id, "verify")}><CheckCircle fontSize="small" /></IconButton></Tooltip>}
                    {p.isVerified && <Tooltip title="Unverify"><IconButton size="small" sx={{color: '#9E9E9E'}} onClick={() => handleAction(p._id, "unverify")}><CheckCircle fontSize="small" /></IconButton></Tooltip>}
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => { setDeleteId(p._id); setDeleteDialog(true); }}><Delete fontSize="small" /></IconButton></Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filtered.length === 0 && <Box py={4} textAlign="center"><Typography color="textSecondary">No partners found</Typography></Box>}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Partner</DialogTitle>
        <DialogContent>
          {editPartner && (
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12}><TextField fullWidth label="Business Name" value={editPartner.businessName || ""} onChange={e => setEditPartner({...editPartner, businessName: e.target.value})} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Owner Name" value={editPartner.ownerName || ""} onChange={e => setEditPartner({...editPartner, ownerName: e.target.value})} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Mobile" value={editPartner.mobile || ""} onChange={e => setEditPartner({...editPartner, mobile: e.target.value})} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Email" value={editPartner.email || ""} onChange={e => setEditPartner({...editPartner, email: e.target.value})} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Business Type" value={editPartner.businessType || ""} onChange={e => setEditPartner({...editPartner, businessType: e.target.value})} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="City" value={editPartner.address?.city || ""} onChange={e => setEditPartner({...editPartner, address: {...(editPartner.address||{}), city: e.target.value}})} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Commission Rate (%)" type="number" value={editPartner.commissionRate !== undefined ? editPartner.commissionRate : 15} onChange={e => setEditPartner({...editPartner, commissionRate: Number(e.target.value)})} InputProps={{inputProps:{min:0,max:100}}} helperText="Platform commission percentage" /></Grid><Grid item xs={6}><TextField fullWidth label="State" value={editPartner.address?.state || ""} onChange={e => setEditPartner({...editPartner, address: {...(editPartner.address||{}), state: e.target.value}})} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setEditDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleEditSave}>Save</Button></DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>⚠️ Delete Partner</DialogTitle>
        <DialogContent><Typography>Are you sure? This will delete the partner, all their properties, and all bookings. This action cannot be undone.</Typography></DialogContent>
        <DialogActions><Button onClick={() => setDeleteDialog(false)}>Cancel</Button><Button variant="contained" color="error" onClick={handleDelete}>Delete</Button></DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Partner Details</DialogTitle>
        <DialogContent>
          {detailPartner && (
            <Box>
              <Typography variant="h6" fontWeight={700}>{detailPartner.businessName}</Typography>
              <Typography color="textSecondary" mb={2}>{detailPartner.businessType}</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}><Typography variant="body2" color="textSecondary">Owner</Typography><Typography fontWeight={600}>{detailPartner.ownerName}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2" color="textSecondary">Mobile</Typography><Typography fontWeight={600}>{detailPartner.mobile}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2" color="textSecondary">Email</Typography><Typography fontWeight={600}>{detailPartner.email || "-"}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2" color="textSecondary">GST</Typography><Typography fontWeight={600}>{detailPartner.gstNumber || "-"}</Typography></Grid>
                <Grid item xs={12}><Typography variant="body2" color="textSecondary">Address</Typography><Typography fontWeight={600}>{[detailPartner.address?.line1 || detailPartner.address?.street, detailPartner.address?.city, detailPartner.address?.state, detailPartner.address?.pincode].filter(Boolean).join(", ") || "-"}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2" color="textSecondary">Approved</Typography><Chip label={detailPartner.isApproved ? "Yes" : "No"} size="small" color={detailPartner.isApproved ? "success" : "warning"} /></Grid>
                <Grid item xs={6}><Typography variant="body2" color="textSecondary">Verified</Typography><Chip label={detailPartner.isVerified ? "Yes" : "No"} size="small" color={detailPartner.isVerified ? "success" : "default"} /></Grid>
                <Grid item xs={12}><Typography variant="body2" color="textSecondary">Bank Details</Typography><Typography fontSize={13}>{detailPartner.bankDetails?.accountName || "-"} | {detailPartner.bankDetails?.bankName || "-"} | {detailPartner.bankDetails?.ifscCode || "-"}</Typography></Grid>
                {detailPartner.panNumber && (<Grid item xs={6}><Typography variant="body2" color="textSecondary">PAN</Typography><Typography fontWeight={600}>{detailPartner.panNumber}</Typography></Grid>)}
                {detailPartner.businessNameHindi && (<Grid item xs={6}><Typography variant="body2" color="textSecondary">Business Name (Hindi)</Typography><Typography fontWeight={600}>{detailPartner.businessNameHindi}</Typography></Grid>)}
                {detailPartner.commissionRate !== undefined && (<Grid item xs={6}><Typography variant="body2" color="textSecondary">Commission Rate</Typography><Typography fontWeight={600}>{detailPartner.commissionRate}%</Typography></Grid>)}
                <Grid item xs={6}><Typography variant="body2" color="textSecondary">Registered</Typography><Typography fontWeight={600}>{detailPartner.createdAt ? new Date(detailPartner.createdAt).toLocaleDateString("en-IN") : "-"}</Typography></Grid>
                {/* Location Section */}
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: detailPartner.location?.latitude ? '#e3f2fd' : '#fff3e0', borderRadius: 2, border: '1px solid', borderColor: detailPartner.location?.latitude ? '#90caf9' : '#ffb74d' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: detailPartner.location?.latitude ? '#1565c0' : '#e65100' }}>📍 GPS Location</Typography>
                    {detailPartner.location?.latitude ? (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>Lat: <b>{Number(detailPartner.location.latitude).toFixed(6)}</b>, Lng: <b>{Number(detailPartner.location.longitude).toFixed(6)}</b></Typography>
                        <a href={'https://www.google.com/maps?q=' + detailPartner.location.latitude + ',' + detailPartner.location.longitude} target="_blank" rel="noopener noreferrer" style={{color:'#1976d2',fontWeight:600,textDecoration:'none'}}>🗺️ Open in Google Maps</a>
                        <Box sx={{ mt: 1, borderRadius: 1, overflow: 'hidden', border: '1px solid #ccc' }}>
                          <iframe title="map" width="100%" height="200" style={{border:0}} loading="lazy" src={'https://maps.google.com/maps?q=' + detailPartner.location.latitude + ',' + detailPartner.location.longitude + '&z=15&output=embed'} />
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="warning.dark">⚠️ Location not captured during registration</Typography>
                    )}
                  </Box>
                </Grid>
                {/* Kitchen / Property Video Section */}
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: detailPartner.kitchenVideoUrl ? '#fff3e0' : '#ffebee', borderRadius: 2, border: '1px solid', borderColor: detailPartner.kitchenVideoUrl ? '#ffb74d' : '#ef9a9a' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: detailPartner.kitchenVideoUrl ? '#e65100' : '#c62828' }}>🍽️ Kitchen / Property Video</Typography>
                    {detailPartner.kitchenVideoUrl ? (
                      <Box>
                        <video controls width="100%" style={{ maxHeight: 320, borderRadius: 6, background: '#000' }} src={'https://api.bharatdarshan.app' + detailPartner.kitchenVideoUrl}>
                          Your browser does not support video playback.
                        </video>
                        <Box sx={{ mt: 1 }}>
                          <a href={'https://api.bharatdarshan.app' + detailPartner.kitchenVideoUrl} target="_blank" rel="noopener noreferrer" style={{color:'#FF6B00',fontWeight:600,textDecoration:'none'}}>↗ Open in new tab</a>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="error.dark">⚠️ Video not uploaded by partner</Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailDialog(false)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({...snack, open: false})} anchorOrigin={{vertical:"top",horizontal:"right"}}>
        <Alert severity={snack.severity} onClose={() => setSnack({...snack, open: false})}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SSPartners;
